import { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseAutocomplete, BaseRow, BaseSelect, BaseUploadFile } from '~/components/common';
import { getIn, useFormik } from 'formik';
import { IEditJobPayload, IJob } from '~/utils/interface/job';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleCreateJob, handleUpdateJobAll } from '~/thunks/job/jobThunk';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, SAVE_TYPES, VALUE_TRUE } from '~/utils/constants/common';
import { cast, generatePersonName, handleApiError, notify } from '~/utils/helper';
import { UploadFile } from '~/utils/types/common';
import { CloseIcon } from '~/assets/icons';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';
import { handleGetLocations } from '~/thunks/location/locationThunk';
import useQuickbook from '~/hooks/useQuickbook';
import { handleGetQuickbookCustomers } from '~/thunks/quickbook/quickbookThunk';
import { jobActions } from '~/thunks/job/jobSlice';
import SelectAssignee from '~/components/common/select-assignee/SelectAssignee';
import accountApi from '~/apis/account';
import { ISelectAccount } from '~/utils/interface/account';
import { CUSTOMER_TYPES_CONTACT, CUSTOMER_TYPES_ORGANIZATION } from '~/utils/constants/customers';
import { ISelectCustomer } from '~/utils/interface/customer';
import { BaseRichEditor } from '~/components/common';
import { IGoogleSearch } from '~/utils/interface/google';
import { handleGetGoogleMapsDetail, handleGetGoogleSearch } from '~/thunks/google/googleThunk';
import classNames from 'classnames/bind';
import styles from './EditJobDialog.module.scss';
import { handleCreateCustomer } from '~/thunks/customer/customerThunk';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_ALL_CUSTOMERS } from '~/utils/constants/master';

const cx = classNames.bind(styles);
type EditJobDialogProps = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  mode: string;
  data?: IJob;
};

const initialValues: IEditJobPayload = {
  customer: {
    customerType: [],
  },
  jobTypeId: '',
  jobPropertyId: '',
  hazard: [],
  sourceOfLossId: '',
  jobCategoryId: '',
  locationId: '',
  note: '',
  isAddressSameCustomer: true,
  address: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  logo: undefined,
  isAfterHours: false,
  referrer: '',
};

const EditJobDialog = (props: EditJobDialogProps) => {
  //#region Destructuring Props
  const { mode = DIALOG_MODE_CREATE } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'admin/common']);
  const dispatch = useAppDispatch();
  const { quickbook } = useQuickbook();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError, setSubmitting }) => {
      const submitValues = { ...values };
      if (mode === DIALOG_MODE_CREATE) {
        try {
          setSubmitting(true);
          await dispatch(handleCreateJob(submitValues)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          dispatch(jobActions.changeNeedRefreshData(true));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        } finally {
          setSubmitting(false); // Reset submitting state
        }
        return;
      }

      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(
            handleUpdateJobAll({
              id: Number(props.data?.id),
              params: { ...submitValues, jobAssigneeIds: values?.jobAssigneeIds?.length ? values.jobAssigneeIds : '' },
            })
          ).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          dispatch(jobActions.changeNeedRefreshData(true));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  //#endregion Declare Hook
  //#region Selector
  const {
    jobTypes,
    jobCategories,
    customerStatuses,
    sourceOfLosses,
    jobProperties,
    prices,
    customers,
    allCustomers,
    referralSources,
    accessCode,
    waterCategory,
    waterClass,
    customerType,
    hazard,
  } = useAppSelector((state) => state.master);
  const { selectionAccounts } = useAppSelector((state) => state.account);
  const { locations } = useAppSelector((state) => state.location);
  const { quickbookCustomers } = useAppSelector((state) => state.quickbook);
  const { options } = useAppSelector((state) => state.google);
  //#endregion Selector

  //#region Declare State
  const [customerQuickbookOption, setCustomerQuickbookOption] = useState<string>(SAVE_TYPES.NO);
  const [assignees, setAssignees] = useState<Array<ISelectAccount>>();

  const [allCustomerGroups, setAllCustomerGroups] = useState<Array<ISelectCustomer>>();
  const [typeCustomer, setTypeCustomer] = useState<string>();

  const newOrExistingOption = useMemo(
    () => [
      {
        id: SAVE_TYPES.NEW,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.NEW}`, { ns: 'admin/common' }),
      },
      {
        id: SAVE_TYPES.EXIST,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.EXIST}`, { ns: 'admin/common' }),
      },
    ],
    [t]
  );
  const onChangeValue = (value: string) => {
    form.setFieldValue('note', value);
  };
  const newOrExistingOptionalOption = useMemo(
    () => [
      {
        id: SAVE_TYPES.NO,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.NO}`, { ns: 'admin/common' }),
      },
      {
        id: SAVE_TYPES.NEW,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.NEW}`, { ns: 'admin/common' }),
      },
      {
        id: SAVE_TYPES.EXIST,
        name: t(`addNewOrExistingOptions.${SAVE_TYPES.EXIST}`, { ns: 'admin/common' }),
      },
    ],
    [t]
  );
  //#endregion Declare State

  useEffect(() => {
    dispatch(handleGetLocations({}));

    dispatch(handleGetAccountsSelection({ roleCodes: ['salesperson'] }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    (async () => {
      try {
        const res = await accountApi.getAccountsSelection({});
        setAssignees(res.data?.data?.responses);
      } catch (e) {
        handleApiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!props.data && locations.length) {
      const location = locations.find((location) => location.isDefault);
      form.setFieldValue('locationId', location?.id ?? locations[0]?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, locations]);

  useEffect(() => {
    if (form.values?.customerOption === SAVE_TYPES.NEW) {
      form.setFieldValue('customer.type', CUSTOMER_TYPES_CONTACT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values?.customerOption]);

  useEffect(() => {
    if (mode === DIALOG_MODE_CREATE && form.values?.customerOption === SAVE_TYPES.NEW) {
      form.setFieldValue('customer.customerType', ['60']);
      quickbook?.isQuickbookConnected && setCustomerQuickbookOption(SAVE_TYPES.NEW);
    } else {
      form.setFieldValue('customer.customerType', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, form.values?.customerOption]);

  useEffect(() => {
    if (allCustomers?.length) {
      const organizations: Array<ISelectCustomer> = [
        {
          id: CUSTOMER_TYPES_ORGANIZATION,
          name: t('editJobDialog.customer.organization'),
          isGroup: true,
        },
      ];

      const contacts: Array<ISelectCustomer> = [
        {
          id: CUSTOMER_TYPES_CONTACT,
          name: t('editJobDialog.customer.contact'),
          isGroup: true,
        },
      ];

      for (let customer of allCustomers) {
        if (customer.type === CUSTOMER_TYPES_ORGANIZATION) {
          organizations.push({
            id: customer?.id,
            name: customer?.name,
          });
        }
        if (customer.type === CUSTOMER_TYPES_CONTACT) {
          contacts.push({
            id: customer?.id,
            name: customer?.name,
          });
        }
      }
      setAllCustomerGroups(organizations.concat(contacts));
    }
  }, [allCustomers, t]);

  useEffect(() => {
    if (quickbook?.isQuickbookConnected) {
      if (customerQuickbookOption === SAVE_TYPES.NO) {
        form.setFieldValue('customer.quickbookCustomerDisplayName', '');
        form.setFieldValue('customer.quickbookCustomer', '');
      }

      if (customerQuickbookOption === SAVE_TYPES.NEW) {
        const quickbookCustomerDisplayName =
          form.values.customer.type === CUSTOMER_TYPES_CONTACT
            ? generatePersonName(form.values.customer?.firstName, form.values.customer?.lastName)
            : form.values.customer?.name;
        form.setFieldValue('customer.quickbookCustomerDisplayName', quickbookCustomerDisplayName);
      }

      if (customerQuickbookOption === SAVE_TYPES.EXIST) {
        dispatch(handleGetQuickbookCustomers())
          .unwrap()
          .catch((e) => {
            handleApiError(e);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerQuickbookOption]);

  useEffect(() => {
    if (quickbook?.isQuickbookConnected && customerQuickbookOption === SAVE_TYPES.NEW) {
      form.setFieldValue(
        'customer.quickbookCustomerDisplayName',
        form.values.customer?.type === CUSTOMER_TYPES_CONTACT
          ? generatePersonName(form.values.customer?.firstName, form.values.customer?.lastName)
          : form.values.customer?.name
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quickbook?.isQuickbookConnected,
    customerQuickbookOption,
    form.values.customer?.firstName,
    form.values.customer?.lastName,
    form.values.customer?.name,
  ]);

  useEffect(() => {
    if (props.data) {
      form.setFieldValue('logo', props.data.logo, false);
      form.setFieldValue('customerOption', SAVE_TYPES.EXIST, false);
      form.setFieldValue('customerId', cast(props.data.customerId));
      form.setFieldValue('name', cast(props.data.name), false);
      // Project Information
      form.setFieldValue('jobTypeId', cast(props.data.jobTypeId), false);
      form.setFieldValue('jobPropertyId', cast(props.data.jobPropertyId), false);
      form.setFieldValue('jobCategoryId', cast(props.data.jobCategoryId), false);
      form.setFieldValue('locationId', cast(props.data.locationId), false);
      form.setFieldValue('yearBuilt', cast(props.data.yearBuilt), false);
      form.setFieldValue('note', cast(props.data.note), false);
      form.setFieldValue('sourceOfLossId', cast(props.data.sourceOfLossId), false);

      form.setFieldValue('hazard', cast(props.data.hazard), false);
      form.setFieldValue('isAfterHours', props.data.isAfterHours ?? false);

      form.setFieldValue('waterCategory', cast(props.data.waterCategory), false);
      form.setFieldValue('waterClass', cast(props.data.waterClass), false);
      form.setFieldValue('accessCode', cast(props.data.accessCode), false);
      form.setFieldValue('code', cast(props.data.code), false);
      form.setFieldValue('referrer', cast(props.data.referrer), false);
      form.setFieldValue('referrerSource', cast(props.data.referrerSource), false);
      form.setFieldValue('locationOfDamage', cast(props.data.locationOfDamage), false);

      form.setFieldValue('isAddressSameCustomer', props.data.isAddressSameCustomer, false);
      setCustomerQuickbookOption(SAVE_TYPES.NO);

      if (!props.data.isAddressSameCustomer) {
        form.setFieldValue('address', cast(props.data.address), false);
        form.setFieldValue('address2', cast(props.data.address2), false);
        form.setFieldValue('city', cast(props.data.city), false);
        form.setFieldValue('state', cast(props.data.state), false);
        form.setFieldValue('zipCode', cast(props.data.zipCode), false);
        form.setFieldValue('country', cast(props.data.country), false);
      }

      form.setFieldValue('priceId', cast(props.data.priceId), false);

      form.setFieldValue('contactInsurance', cast(props.data.contactInsurance), false);
      const jobInsurance = props.data.jobInsurance;
      if (jobInsurance) {
        form.setFieldValue('jobInsurance.companyName', cast(jobInsurance.companyName), false);
        form.setFieldValue('jobInsurance.policy', cast(jobInsurance.policy), false);
        form.setFieldValue('jobInsurance.claim', cast(jobInsurance.claim), false);
        form.setFieldValue('jobInsurance.lossAdjusterReference', cast(jobInsurance.lossAdjusterReference), false);
        form.setFieldValue('jobInsurance.adjusterName', cast(jobInsurance.adjusterName), false);
        form.setFieldValue('jobInsurance.adjusterLastName', cast(jobInsurance.adjusterLastName), false);
        form.setFieldValue('jobInsurance.adjusterPhone', cast(jobInsurance.adjusterPhone), false);
        form.setFieldValue('jobInsurance.adjusterEmail', cast(jobInsurance.adjusterEmail), false);
        form.setFieldValue('jobInsurance.agentName', cast(jobInsurance.agentName), false);
        form.setFieldValue('jobInsurance.agentLastName', cast(jobInsurance.agentLastName), false);
        form.setFieldValue('jobInsurance.agentPhone', cast(jobInsurance.agentPhone), false);
        form.setFieldValue('jobInsurance.agentEmail', cast(jobInsurance.agentEmail), false);
        form.setFieldValue('jobInsurance.agentAgencyName', cast(jobInsurance.agentAgencyName), false);
      }

      if (props.data?.assignees?.length) {
        form.setFieldValue(
          'jobAssigneeIds',
          props.data.assignees.map((assignee) => assignee.id),
          false
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    setTypeCustomer(undefined);
    props.setOpen(open, needRefreshData);
  };

  const handleSearch = async (inputValue: string) => {
    dispatch(handleGetGoogleSearch({ query: inputValue }));
  };

  const handleSelect = async (selectedOption: IGoogleSearch) => {
    const res = await dispatch(handleGetGoogleMapsDetail({ query: selectedOption.placeId }));
    form.setFieldValue('customer.address', cast(res?.payload.address), false);
    form.setFieldValue('customer.city', cast(res?.payload.city), false);
    form.setFieldValue('customer.state', cast(res?.payload.state), false);
    form.setFieldValue('customer.zipCode', cast(res?.payload.zipCode), false);
    form.setFieldValue('customer.country', cast(res?.payload.country), false);
  };

  const handleChangeText = (text: string) => {
    form.setFieldValue('customer.address', cast(text), false);
  };

  const handleProjectChangeText = (text: string) => {
    form.setFieldValue('address', cast(text), false);
  };

  const handleSelectEdit = async (selectedOption: IGoogleSearch) => {
    const res = await dispatch(handleGetGoogleMapsDetail({ query: selectedOption.placeId }));
    const address = res?.payload.address?.trim() !== '' ? res.payload.address : res.payload.fullAddress;
    form.setFieldValue('address', cast(address), false);
    form.setFieldValue('city', cast(res?.payload.city), false);
    form.setFieldValue('state', cast(res?.payload.state), false);
    form.setFieldValue('zipCode', cast(res?.payload.zipCode), false);
    form.setFieldValue('country', cast(res?.payload.country), false);
  };

  const handleTopComponent = (text: string) => {
    return (
      <Box className='flex flex-col items-start'>
        {text && (
          <>
            <Button
              onClick={() => handleClickHeader(CUSTOMER_TYPES_CONTACT, text)}
              className={cx('select-click', 'block', 'text-left', 'w-full')}
            >
              {t('addNewContact')}
            </Button>
            <Button
              onClick={() => handleClickHeader(CUSTOMER_TYPES_ORGANIZATION, text)}
              className={cx('select-click', 'block', 'text-left', 'w-full')}
            >
              {t('addNewOrganization')}
            </Button>
          </>
        )}
      </Box>
    );
  };

  const handleClickHeader = async (item: string, text?: string) => {
    let firstName = text;
    let lastName = '';

    if (item === CUSTOMER_TYPES_CONTACT) {
      const textArray = (text || '').split(' ');
      if (textArray.length === 2) {
        firstName = textArray[0];
        lastName = textArray[1];
      }
    }

    await dispatch(
      handleCreateCustomer({ name: text, firstName, lastName, type: item, isAddressSameParent: true, isCrm: true })
    ).unwrap();
    notify('success', t('createSuccess', { ns: 'admin/common' }));
    await fetchMasterData();
  };

  const fetchMasterData = async () => {
    try {
      await dispatch(
        handleGetMasterData({
          type: [MASTER_DATA_ALL_CUSTOMERS],
        })
      ).unwrap();
    } catch (e) {
      handleApiError(e);
    }
  };
  //#endregion Handle Function
  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('editJobDialog.addTitle') : t('editJobDialog.editTitle')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('editJobDialog.job.name')}</InputLabel>
              <TextField
                name='name'
                placeholder={mode === DIALOG_MODE_EDIT ? t('editJobDialog.job.name') : t('editJobDialog.job.auto')}
                value={cast(form.values?.name)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'name'))}
                helperText={getIn(form.errors, 'name')}
              />
            </Box>
          </BaseRow>

          {/* Upload logo */}
          <Box className='gap-[20px] mb-[20px]'>
            <InputLabel>{t('editJobDialog.job.logo')}</InputLabel>
            <BaseUploadFile
              uploadedFiles={typeof form.values.logo === 'string' ? [{ url: form.values.logo }] : undefined}
              onChange={(files: Array<UploadFile>) => {
                form.setFieldValue('logo', files[0]?.file ?? '');
              }}
            />
          </Box>

          {/* Customer information */}
          <Box className='text-[#000000] mb-[20px]'>{t('editJobDialog.customer.customerInfo')}</Box>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel required={true}>{t('editJobDialog.customer.option')}</InputLabel>
              <BaseSelect
                name='customerOption'
                value={cast(form.values?.customerOption)}
                options={newOrExistingOption}
                label={t('editJobDialog.customer.chooseOne')}
                onChange={(e) => {
                  form.handleChange(e);
                  setTypeCustomer(CUSTOMER_TYPES_CONTACT);
                }}
                error={Boolean(getIn(form.errors, 'customerOption'))}
                helperText={getIn(form.errors, 'customerOption')}
              />
            </Box>
            {form.values?.customerOption === SAVE_TYPES.EXIST && (
              <>
                <Box className='w-full'>
                  <InputLabel required={true}>{t('editJobDialog.customer.contact')}</InputLabel>
                  <BaseSelect
                    name='customerId'
                    options={allCustomerGroups}
                    value={cast(form.values?.customerId)}
                    onChange={(event) => {
                      const selectedId = event.target.value;

                      const selectedCustomer = allCustomerGroups?.find((group) => group.id === selectedId);

                      if (selectedCustomer) {
                        const customerDetails = allCustomers?.find((customer) => customer.id === selectedCustomer.id);

                        if (customerDetails) {
                          if (customerDetails.type === CUSTOMER_TYPES_ORGANIZATION) {
                            setTypeCustomer(CUSTOMER_TYPES_ORGANIZATION);
                          } else if (customerDetails.type === CUSTOMER_TYPES_CONTACT) {
                            setTypeCustomer(CUSTOMER_TYPES_CONTACT);
                          }
                        }
                      }
                      form.handleChange(event);
                    }}
                    error={Boolean(form.errors?.customerId)}
                    helperText={form.errors?.customerId}
                    isSearchEnable={true}
                  />
                </Box>
                <Box className='w-full'></Box>
              </>
            )}

            {!form.values?.customerOption && (
              <>
                <Box className='w-full'></Box>
                <Box className='w-full'></Box>
              </>
            )}

            {form.values?.customerOption === SAVE_TYPES.NEW && (
              <>
                <Box className='w-full'>
                  <InputLabel required={true}>{t('editJobDialog.customer.customerType')}</InputLabel>
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby='customer-type'
                      name='customer.type'
                      value={form.values.customer?.type ?? CUSTOMER_TYPES_CONTACT}
                      onChange={(e) => {
                        form.handleChange(e);
                        const value = e.target.value;
                        setTypeCustomer(value);
                      }}
                    >
                      <FormControlLabel
                        value={CUSTOMER_TYPES_ORGANIZATION}
                        control={<Radio />}
                        label={t('editJobDialog.customer.types.organization')}
                      />
                      <FormControlLabel
                        value={CUSTOMER_TYPES_CONTACT}
                        control={<Radio />}
                        label={t('editJobDialog.customer.types.contact')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                <Box className='w-full'></Box>
              </>
            )}
          </BaseRow>
          {form.values?.customerOption === SAVE_TYPES.NEW && (
            <>
              <BaseRow className='gap-[20px] mb-[20px]'>
                {form.values.customer.type === CUSTOMER_TYPES_ORGANIZATION && (
                  <Box className='w-full'>
                    <InputLabel required={true}>{t('editJobDialog.customer.name')}</InputLabel>
                    <TextField
                      name='customer.name'
                      placeholder={t('editJobDialog.customer.name')}
                      value={cast(form.values?.customer?.name)}
                      onChange={form.handleChange}
                      error={Boolean(
                        typeof form.errors.customer === 'string'
                          ? form.errors.customer
                          : getIn(form.errors, 'customer.name')
                      )}
                      helperText={
                        typeof form.errors.customer === 'string'
                          ? form.errors.customer
                          : getIn(form.errors, 'customer.name')
                      }
                    />
                  </Box>
                )}
                {form.values.customer.type === CUSTOMER_TYPES_CONTACT && (
                  <>
                    <Box className='w-full'>
                      <InputLabel required={true}>{t('editJobDialog.customer.firstName')}</InputLabel>
                      <TextField
                        name='customer.firstName'
                        placeholder={t('editJobDialog.customer.firstName')}
                        value={cast(form.values?.customer?.firstName)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'customer.firstName'))}
                        helperText={getIn(form.errors, 'customer.firstName')}
                      />
                    </Box>
                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.lastName')}</InputLabel>
                      <TextField
                        name='customer.lastName'
                        placeholder={t('editJobDialog.customer.lastName')}
                        value={cast(form.values?.customer?.lastName)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'customer.lastName'))}
                        helperText={getIn(form.errors, 'customer.lastName')}
                      />
                    </Box>
                  </>
                )}
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.customer.relationshipType')}</InputLabel>
                  {/* <TextField
                    name='customer.relationshipType'
                    placeholder={t('editJobDialog.customer.relationshipType')}
                    value={cast(form.values?.customer?.relationshipType)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'customer.relationshipType'))}
                    helperText={getIn(form.errors, 'customer.relationshipType')}
                  /> */}
                  <BaseSelect
                    name='customer.customerType'
                    value={form.values.customer?.customerType}
                    options={customerType}
                    label={t('editJobDialog.customer.relationshipType')}
                    onChange={(e) => {
                      form.setFieldValue('customer.customerType', e.target.value);
                    }}
                    error={Boolean(form.errors?.customer?.customerType)}
                    multiple
                  />
                </Box>
                {form.values.customer.type === CUSTOMER_TYPES_ORGANIZATION && <Box className='w-full'></Box>}
              </BaseRow>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.customer.email')}</InputLabel>
                  <TextField
                    name='customer.email'
                    placeholder={t('editJobDialog.customer.email')}
                    value={cast(form.values?.customer?.email)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'customer.email'))}
                    helperText={getIn(form.errors, 'customer.email')}
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.customer.phone')}</InputLabel>
                  <TextField
                    name='customer.phone'
                    placeholder={t('editJobDialog.customer.phone')}
                    value={cast(form.values?.customer?.phone)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'customer.phone'))}
                    helperText={getIn(form.errors, 'customer.phone')}
                  />
                </Box>
                {form.values.customer.type === CUSTOMER_TYPES_CONTACT && (
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.customer.organization')}</InputLabel>
                    <BaseSelect
                      name='customer.parentId'
                      options={customers}
                      label={t('editJobDialog.customer.organization')}
                      value={cast(form.values?.customer?.parentId)}
                      onChange={form.handleChange}
                      isSearchEnable={true}
                      error={Boolean(getIn(form.errors, 'customer.parentId'))}
                      helperText={getIn(form.errors, 'customer.parentId')}
                    />
                  </Box>
                )}
                {form.values.customer.type === CUSTOMER_TYPES_ORGANIZATION && <Box className='w-full'></Box>}
              </BaseRow>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.customer.status')}</InputLabel>
                  <BaseSelect
                    name='customer.statusId'
                    options={customerStatuses}
                    isHideIconCheckbox={true}
                    label={t('editJobDialog.customer.status')}
                    value={cast(form.values?.customer?.statusId)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'customer.statusId'))}
                    helperText={getIn(form.errors, 'customer.statusId')}
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.customer.salePerson')}</InputLabel>
                  <BaseSelect
                    name='customer.salespersonId'
                    options={selectionAccounts}
                    isHideIconCheckbox={true}
                    label={t('editJobDialog.customer.salePerson')}
                    value={cast(form.values?.customer?.salespersonId)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'customer.salespersonId'))}
                    helperText={getIn(form.errors, 'customer.salespersonId')}
                    hasDefaultOption={true}
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.customer.referralResource')}</InputLabel>
                  <BaseSelect
                    name='customer.referralSourceId'
                    options={referralSources}
                    isHideIconCheckbox={true}
                    label={t('editJobDialog.customer.referralResource')}
                    value={cast(form.values?.customer?.referralSourceId)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'customer.referralSourceId'))}
                    helperText={getIn(form.errors, 'customer.referralSourceId')}
                  />
                </Box>
              </BaseRow>

              {!form.values.customer?.isAddressSameParent && (
                <>
                  <BaseRow className='gap-[20px] mb-[20px]'>
                    <Box className={cx('box-input')}>
                      <InputLabel>{t('editJobDialog.customer.address1')}</InputLabel>
                      <BaseAutocomplete
                        value={
                          getIn(form.values, 'customer.address')
                            ? {
                                id: '1',
                                name: getIn(form.values, 'customer.address'),
                                group: 'Address',
                              }
                            : null
                        }
                        onSearch={handleSearch}
                        onSelected={handleSelect}
                        onChangeText={handleChangeText}
                        options={options}
                      />
                    </Box>
                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.address2')}</InputLabel>
                      <TextField
                        name='customer.address2'
                        placeholder={t('editJobDialog.customer.address2')}
                        value={cast(form.values?.customer?.address2)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'customer.address2'))}
                        helperText={getIn(form.errors, 'customer.address2')}
                      />
                    </Box>
                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.city')}</InputLabel>
                      <TextField
                        name='customer.city'
                        placeholder={t('editJobDialog.customer.city')}
                        value={cast(form.values?.customer?.city)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'customer.city'))}
                        helperText={getIn(form.errors, 'customer.city')}
                      />
                    </Box>
                  </BaseRow>
                  <BaseRow className='gap-[20px] mb-[20px]'>
                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.state')}</InputLabel>
                      <TextField
                        name='customer.state'
                        placeholder={t('editJobDialog.customer.state')}
                        value={cast(form.values?.customer?.state)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'customer.state'))}
                        helperText={getIn(form.errors, 'customer.state')}
                      />
                    </Box>
                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.zipCode')}</InputLabel>
                      <TextField
                        name='customer.zipCode'
                        placeholder={t('editJobDialog.customer.zipCode')}
                        value={cast(form.values?.customer?.zipCode)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'customer.zipCode'))}
                        helperText={getIn(form.errors, 'customer.zipCode')}
                      />
                    </Box>
                    <Box className='w-full'>
                      {/* <InputLabel>{t('editJobDialog.customer.country')}</InputLabel> */}
                      {/* <TextField
                        name='customer.country'
                        placeholder={t('editJobDialog.customer.country')}
                        value={cast(form.values?.customer?.country)}
                        onChange={form.handleChange}
                        error={Boolean(getIn(form.errors, 'customer.country'))}
                        helperText={getIn(form.errors, 'customer.country')}
                      /> */}
                    </Box>
                  </BaseRow>
                </>
              )}
            </>
          )}

          {/* Project Information */}
          <Box className='text-[#000000] mb-[20px]'>{t('editJobDialog.job.projectInformation')}</Box>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('editJobDialog.job.jobType')}</InputLabel>
              <BaseSelect
                name='jobTypeId'
                placeholder={t('editJobDialog.job.jobType')}
                value={cast(form.values.jobTypeId)}
                options={jobTypes}
                onChange={(value) => form.setFieldValue('jobTypeId', value.target.value)}
                error={Boolean(form.errors.jobTypeId)}
                helperText={form.errors.jobTypeId}
              ></BaseSelect>
            </Box>
            <Box className='w-full'>
              <InputLabel required={true}>{t('editJobDialog.job.propertyType')}</InputLabel>
              <BaseSelect
                name='jobPropertyId'
                placeholder={t('editJobDialog.job.propertyType')}
                value={cast(form.values.jobPropertyId)}
                options={jobProperties}
                onChange={(value) => form.setFieldValue('jobPropertyId', value.target.value)}
                error={Boolean(form.errors.jobPropertyId)}
                helperText={form.errors.jobPropertyId}
              ></BaseSelect>
            </Box>
            <Box className='w-full'>
              <InputLabel>{t('editJobDialog.job.projectsubType')}</InputLabel>
              <BaseSelect
                name='jobCategoryId'
                placeholder={t('editJobDialog.job.projectsubType')}
                value={cast(form.values.jobCategoryId)}
                options={jobCategories}
                onChange={(value) => form.setFieldValue('jobCategoryId', value.target.value)}
                error={Boolean(form.errors.jobCategoryId)}
                helperText={form.errors.jobCategoryId}
              ></BaseSelect>
            </Box>
          </BaseRow>
          {mode === DIALOG_MODE_EDIT && (
            <>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel className='mt-[20px]'></InputLabel>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.values.isAfterHours}
                        onChange={(e) => {
                          form.setFieldValue('isAfterHours', e.target.checked);
                        }}
                      />
                    }
                    label={t('properties.isAfterHours')}
                    name='isAfterHours'
                    value={true}
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('properties.waterCategory')}</InputLabel>
                  <BaseSelect
                    name='waterCategory'
                    placeholder={t('properties.waterCategory')}
                    value={cast(form.values.waterCategory)}
                    options={waterCategory}
                    onChange={form.handleChange}
                    error={Boolean(form.errors.waterCategory)}
                    helperText={form.errors.waterCategory}
                  ></BaseSelect>
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('properties.waterClass')}</InputLabel>
                  <BaseSelect
                    name='waterClass'
                    placeholder={t('properties.waterClass')}
                    value={cast(form.values.waterClass)}
                    options={waterClass}
                    onChange={form.handleChange}
                    error={Boolean(form.errors.waterClass)}
                    helperText={form.errors.waterClass}
                  ></BaseSelect>
                </Box>
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>{t('properties.locationOfDamage')}</InputLabel>
                  <TextField
                    name='locationOfDamage'
                    placeholder={t('properties.locationOfDamage')}
                    value={cast(form.values?.locationOfDamage)}
                    onChange={form.handleChange}
                    error={Boolean(form.errors.locationOfDamage)}
                    helperText={form.errors.locationOfDamage}
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('properties.hazard')}</InputLabel>
                  <BaseSelect
                    name='hazard'
                    value={form.values.hazard}
                    options={hazard}
                    onChange={(e) => {
                      form.setFieldValue('hazard', e.target.value);
                    }}
                    error={Boolean(form.errors?.hazard)}
                    multiple
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.job.priceList')}</InputLabel>
                  <BaseSelect
                    name='priceId'
                    placeholder={t('editJobDialog.job.priceList')}
                    value={cast(form.values.priceId)}
                    options={prices}
                    onChange={form.handleChange}
                    error={Boolean(form.errors.priceId)}
                    helperText={form.errors.priceId}
                  ></BaseSelect>
                </Box>
              </BaseRow>
            </>
          )}
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('editJobDialog.job.location')}</InputLabel>
              <BaseSelect
                name='locationId'
                placeholder={t('editJobDialog.job.location')}
                value={cast(form.values.locationId)}
                options={locations}
                onChange={(value) => form.setFieldValue('locationId', value.target.value)}
                error={Boolean(form.errors.locationId)}
                helperText={form.errors.locationId}
              ></BaseSelect>
            </Box>

            <Box className='w-full'>
              <InputLabel>{t('editJobDialog.job.sourceOfLoss')}</InputLabel>
              <BaseSelect
                name='sourceOfLossId'
                placeholder={t('editJobDialog.job.sourceOfLoss')}
                value={cast(form.values.sourceOfLossId)}
                options={sourceOfLosses}
                onChange={(value) => form.setFieldValue('sourceOfLossId', value.target.value)}
                error={Boolean(form.errors.sourceOfLossId)}
                helperText={form.errors.sourceOfLossId}
              ></BaseSelect>
            </Box>

            {mode === DIALOG_MODE_EDIT && (
              <Box className='w-full'>
                <InputLabel>{t('editJobDialog.job.yearBuilt')}</InputLabel>
                <TextField
                  name='yearBuilt'
                  placeholder={t('editJobDialog.job.yearBuilt')}
                  value={cast(form.values.yearBuilt)}
                  onChange={form.handleChange}
                  error={Boolean(form.errors.yearBuilt)}
                  helperText={form.errors.yearBuilt}
                ></TextField>
              </Box>
            )}
          </BaseRow>

          {typeCustomer ? (
            <>
              <BaseRow className='mb-[20px]'>
                <Box className='flex'>
                  <FormControlLabel
                    control={<Checkbox checked={form.values.isAddressSameCustomer} onChange={form.handleChange} />}
                    label={
                      typeCustomer === CUSTOMER_TYPES_ORGANIZATION
                        ? t('editJobDialog.customer.sameasaddressOrganization')
                        : t('editJobDialog.customer.sameasaddressContact')
                    }
                    name='isAddressSameCustomer'
                    value={true}
                  />
                </Box>
              </BaseRow>
              {!form.values.isAddressSameCustomer && (
                <>
                  <BaseRow className='gap-[20px] mb-[20px]'>
                    <Box className={cx('box-input')}>
                      <InputLabel>{t('editJobDialog.customer.address1')}</InputLabel>
                      <BaseAutocomplete
                        value={
                          getIn(form.values, 'address')
                            ? {
                                id: '1',
                                name: getIn(form.values, 'address'),
                                group: 'Location',
                              }
                            : null
                        }
                        onSearch={handleSearch}
                        onChangeText={handleProjectChangeText}
                        onSelected={handleSelectEdit}
                        options={options}
                      />
                    </Box>

                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.address2')}</InputLabel>
                      <TextField
                        name='address2'
                        placeholder={t('editJobDialog.customer.address2')}
                        value={cast(form.values.address2)}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.address2)}
                        helperText={form.errors.address2}
                      />
                    </Box>

                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.city')}</InputLabel>
                      <TextField
                        name='city'
                        placeholder={t('editJobDialog.customer.city')}
                        value={cast(form.values.city)}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.city)}
                        helperText={form.errors.city}
                      />
                    </Box>
                  </BaseRow>

                  <BaseRow className='gap-[20px] mb-[20px]'>
                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.state')}</InputLabel>
                      <TextField
                        name='state'
                        placeholder={t('editJobDialog.customer.state')}
                        value={cast(form.values.state)}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.state)}
                        helperText={form.errors.state}
                      />
                    </Box>

                    <Box className='w-full'>
                      <InputLabel>{t('editJobDialog.customer.zipCode')}</InputLabel>
                      <TextField
                        name='zipCode'
                        placeholder={t('editJobDialog.customer.zipCode')}
                        value={cast(form.values.zipCode)}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.zipCode)}
                        helperText={form.errors.zipCode}
                      />
                    </Box>

                    <Box className='w-full'></Box>
                  </BaseRow>
                </>
              )}
            </>
          ) : null}

          {mode === DIALOG_MODE_EDIT && (
            <>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.job.referrer')}</InputLabel>
                  <BaseSelect
                    name='referrer'
                    options={allCustomerGroups}
                    value={cast(form.values?.referrer)}
                    onChange={(event) => {
                      form.handleChange(event);
                    }}
                    error={Boolean(form.errors.referrer)}
                    helperText={form.errors.referrer}
                    isSearchEnable={true}
                    topComponent={handleTopComponent}
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel>{t('editJobDialog.customer.referralResource')}</InputLabel>
                  <BaseSelect
                    name='referrerSource'
                    options={referralSources}
                    isHideIconCheckbox={true}
                    label={t('editJobDialog.customer.referralResource')}
                    value={form.values?.referrerSource}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'referrerSource'))}
                    helperText={getIn(form.errors, 'referrerSource')}
                  />
                </Box>
                <Box className='w-full'></Box>
              </BaseRow>
            </>
          )}

          {mode === DIALOG_MODE_CREATE && (
            <>
              <Box className='mb-[20px]'>
                <InputLabel className='mb-[20px]'>{t('activitiesNotes.notes')}</InputLabel>
                <BaseRichEditor
                  onCreateNew={onChangeValue}
                  valueProps={form.values.note}
                  isError={Boolean(form.errors?.note)}
                  errorMessage={form.errors.note}
                />
              </Box>
              {form.values?.customerOption === SAVE_TYPES.NEW && (
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.customer.linktoQuickbookCustomer')}</InputLabel>
                    <BaseSelect
                      value={customerQuickbookOption}
                      options={newOrExistingOptionalOption}
                      onChange={(e) => {
                        setCustomerQuickbookOption(e.target.value);
                      }}
                    />
                  </Box>
                  <Box className='w-full'>
                    {customerQuickbookOption === SAVE_TYPES.NEW && (
                      <>
                        <InputLabel required={true}>{t('properties.quickbookCustomerDisplayName')}</InputLabel>
                        {quickbook?.isQuickbookConnected ? (
                          <TextField
                            name='customer.quickbookCustomerDisplayName'
                            value={cast(form.values?.customer?.quickbookCustomerDisplayName)}
                            onChange={form.handleChange}
                            error={Boolean(getIn(form.errors, 'customer.quickbookCustomerDisplayName'))}
                            helperText={getIn(form.errors, 'customer.quickbookCustomerDisplayName')}
                          />
                        ) : (
                          <Typography color='error'>
                            {t('editJobDialog.customer.pleaseConnectToQuickbooksFirst')}
                          </Typography>
                        )}
                      </>
                    )}

                    {customerQuickbookOption === SAVE_TYPES.EXIST && (
                      <>
                        <InputLabel>{t('properties.quickbookCustomer')}</InputLabel>
                        {quickbook?.isQuickbookConnected ? (
                          <BaseSelect
                            name='customer.quickbookCustomer'
                            value={cast(form.values?.customer?.quickbookCustomer)}
                            options={quickbookCustomers}
                            onChange={form.handleChange}
                            error={Boolean(getIn(form.errors, 'customer.quickbookCustomer'))}
                            helperText={getIn(form.errors, 'customer.quickbookCustomer')}
                          />
                        ) : (
                          <Typography color='error'>
                            {t('editJobDialog.customer.pleaseConnectToQuickbooksFirst')}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  <Box className='w-full'></Box>
                </BaseRow>
              )}
            </>
          )}
        </Box>

        {/* For EDIT MODE */}
        {mode === DIALOG_MODE_EDIT && (
          <>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('properties.accessCode')}</InputLabel>
                <BaseSelect
                  name='accessCode'
                  placeholder={t('properties.accessCode')}
                  value={cast(form.values.accessCode)}
                  options={accessCode}
                  onChange={form.handleChange}
                  error={Boolean(form.errors.accessCode)}
                  helperText={form.errors.accessCode}
                ></BaseSelect>
              </Box>
              {form.values.accessCode && (
                <Box className='w-full'>
                  <InputLabel>{t('properties.code')}</InputLabel>
                  <TextField
                    name='code'
                    placeholder={t('properties.code')}
                    value={cast(form.values?.code)}
                    onChange={form.handleChange}
                    error={Boolean(form.errors.code)}
                    helperText={form.errors.code}
                  />
                </Box>
              )}
              <Box className='w-full'></Box>
            </BaseRow>
            {props.data?.customer.type === CUSTOMER_TYPES_ORGANIZATION ? (
              <BaseRow className='mb-[20px] '>
                <Box className='flex'>
                  <FormControlLabel
                    control={<Checkbox checked={form.values.isAddressSameCustomer} onChange={form.handleChange} />}
                    label={t('editJobDialog.customer.sameasaddressOrganization')}
                    name='isAddressSameCustomer'
                    value={true}
                  />
                </Box>
              </BaseRow>
            ) : (
              <BaseRow className='mb-[20px] '>
                <Box className='flex'>
                  <FormControlLabel
                    control={<Checkbox checked={form.values.isAddressSameCustomer} onChange={form.handleChange} />}
                    label={t('editJobDialog.customer.sameasaddressContact')}
                    name='isAddressSameCustomer'
                    value={true}
                  />
                </Box>
              </BaseRow>
            )}

            {!form.values.isAddressSameCustomer && (
              <>
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className={cx('box-input')}>
                    <InputLabel>{t('editJobDialog.customer.address1')}</InputLabel>
                    <BaseAutocomplete
                      value={
                        getIn(form.values, 'address')
                          ? {
                              id: '1',
                              name: getIn(form.values, 'address'),
                              group: 'Location',
                            }
                          : null
                      }
                      onSearch={handleSearch}
                      onChangeText={handleProjectChangeText}
                      onSelected={handleSelectEdit}
                      options={options}
                    />
                  </Box>

                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.customer.address2')}</InputLabel>
                    <TextField
                      name='address2'
                      placeholder={t('editJobDialog.customer.address2')}
                      value={cast(form.values.address2)}
                      onChange={form.handleChange}
                      error={Boolean(form.errors.address2)}
                      helperText={form.errors.address2}
                    />
                  </Box>

                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.customer.city')}</InputLabel>
                    <TextField
                      name='city'
                      placeholder={t('editJobDialog.customer.city')}
                      value={cast(form.values.city)}
                      onChange={form.handleChange}
                      error={Boolean(form.errors.city)}
                      helperText={form.errors.city}
                    />
                  </Box>
                </BaseRow>

                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.customer.state')}</InputLabel>
                    <TextField
                      name='state'
                      placeholder={t('editJobDialog.customer.state')}
                      value={cast(form.values.state)}
                      onChange={form.handleChange}
                      error={Boolean(form.errors.state)}
                      helperText={form.errors.state}
                    />
                  </Box>

                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.customer.zipCode')}</InputLabel>
                    <TextField
                      name='zipCode'
                      placeholder={t('editJobDialog.customer.zipCode')}
                      value={cast(form.values.zipCode)}
                      onChange={form.handleChange}
                      error={Boolean(form.errors.zipCode)}
                      helperText={form.errors.zipCode}
                    />
                  </Box>

                  <Box className='w-full'></Box>
                </BaseRow>
              </>
            )}
            <Box className='mb-[20px]'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.values.contactInsurance === VALUE_TRUE}
                    onChange={(e) => form.setFieldValue('contactInsurance', cast(e.target.checked))}
                  />
                }
                label={t('editJobDialog.jobInsurance.insuranceTitle')}
                name='contactInsurance'
                value={VALUE_TRUE}
              />
            </Box>
            {form.values.contactInsurance === VALUE_TRUE && (
              <>
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel required={true}>{t('editJobDialog.jobInsurance.companyName')}</InputLabel>
                    <TextField
                      name='jobInsurance.companyName'
                      placeholder={t('editJobDialog.jobInsurance.companyName')}
                      value={cast(form.values?.jobInsurance?.companyName)}
                      onChange={form.handleChange}
                      error={Boolean(
                        typeof form.errors.jobInsurance === 'string'
                          ? form.errors.jobInsurance
                          : getIn(form.errors, 'jobInsurance.companyName')
                      )}
                      helperText={
                        typeof form.errors.jobInsurance === 'string'
                          ? form.errors.jobInsurance
                          : getIn(form.errors, 'jobInsurance.companyName')
                      }
                    />
                  </Box>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.policy')}</InputLabel>
                    <TextField
                      name='jobInsurance.policy'
                      placeholder={t('editJobDialog.jobInsurance.policy')}
                      value={cast(form.values?.jobInsurance?.policy)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.policy'))}
                      helperText={getIn(form.errors, 'jobInsurance.policy')}
                    />
                  </Box>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.claim')}</InputLabel>
                    <TextField
                      name='jobInsurance.claim'
                      placeholder={t('editJobDialog.jobInsurance.claim')}
                      value={cast(form.values?.jobInsurance?.claim)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.claim'))}
                      helperText={getIn(form.errors, 'jobInsurance.claim')}
                    />
                  </Box>
                </BaseRow>
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.lossAdjusterReference')}</InputLabel>
                    <TextField
                      name='jobInsurance.lossAdjusterReference'
                      placeholder={t('editJobDialog.jobInsurance.lossAdjusterReference')}
                      value={cast(form.values?.jobInsurance?.lossAdjusterReference)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.lossAdjusterReference'))}
                      helperText={getIn(form.errors, 'jobInsurance.lossAdjusterReference')}
                    />
                  </Box>
                  <Box className='w-full'></Box>
                  <Box className='w-full'></Box>
                </BaseRow>
                <Box className='mb-[20px]'>{t('editJobDialog.jobInsurance.insuranceAdjusterContactTitle')}</Box>
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.adjusterName')}</InputLabel>
                    <TextField
                      name='jobInsurance.adjusterName'
                      placeholder={t('editJobDialog.jobInsurance.adjusterName')}
                      value={cast(form.values?.jobInsurance?.adjusterName)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.adjusterName'))}
                      helperText={getIn(form.errors, 'jobInsurance.adjusterName')}
                    />
                  </Box>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.adjusterLastName')}</InputLabel>
                    <TextField
                      name='jobInsurance.adjusterLastName'
                      placeholder={t('editJobDialog.jobInsurance.adjusterLastName')}
                      value={cast(form.values?.jobInsurance?.adjusterLastName)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.adjusterLastName'))}
                      helperText={getIn(form.errors, 'jobInsurance.adjusterLastName')}
                    />
                  </Box>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.adjusterEmail')}</InputLabel>
                    <TextField
                      name='jobInsurance.adjusterEmail'
                      placeholder={t('editJobDialog.jobInsurance.adjusterEmail')}
                      value={cast(form.values?.jobInsurance?.adjusterEmail)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.adjusterEmail'))}
                      helperText={getIn(form.errors, 'jobInsurance.adjusterEmail')}
                    />
                  </Box>
                </BaseRow>
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.adjusterPhone')}</InputLabel>
                    <TextField
                      name='jobInsurance.adjusterPhone'
                      placeholder={t('editJobDialog.jobInsurance.adjusterPhone')}
                      value={cast(form.values?.jobInsurance?.adjusterPhone)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.adjusterPhone'))}
                      helperText={getIn(form.errors, 'jobInsurance.adjusterPhone')}
                    />
                  </Box>
                  <Box className='w-full'></Box>
                  <Box className='w-full'></Box>
                </BaseRow>

                <Box className='mb-[20px]'>{t('editJobDialog.jobInsurance.insuranceAgentContactTitle')}</Box>
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.agentAgencyName')}</InputLabel>
                    <TextField
                      name='jobInsurance.agentAgencyName'
                      placeholder={t('editJobDialog.jobInsurance.agentAgencyName')}
                      value={cast(form.values?.jobInsurance?.agentAgencyName)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.agentAgencyName'))}
                      helperText={getIn(form.errors, 'jobInsurance.agentAgencyName')}
                    />
                  </Box>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.agentName')}</InputLabel>
                    <TextField
                      name='jobInsurance.agentName'
                      placeholder={t('editJobDialog.jobInsurance.agentName')}
                      value={cast(form.values?.jobInsurance?.agentName)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.agentName'))}
                      helperText={getIn(form.errors, 'jobInsurance.agentName')}
                    />
                  </Box>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.agentLastName')}</InputLabel>
                    <TextField
                      name='jobInsurance.agentLastName'
                      placeholder={t('editJobDialog.jobInsurance.agentLastName')}
                      value={cast(form.values?.jobInsurance?.agentLastName)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.agentLastName'))}
                      helperText={getIn(form.errors, 'jobInsurance.agentLastName')}
                    />
                  </Box>
                </BaseRow>
                <BaseRow className='gap-[20px] mb-[20px]'>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.agentEmail')}</InputLabel>
                    <TextField
                      name='jobInsurance.agentEmail'
                      placeholder={t('editJobDialog.jobInsurance.agentEmail')}
                      value={cast(form.values?.jobInsurance?.agentEmail)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.agentEmail'))}
                      helperText={getIn(form.errors, 'jobInsurance.agentEmail')}
                    />
                  </Box>
                  <Box className='w-full'>
                    <InputLabel>{t('editJobDialog.jobInsurance.agentPhone')}</InputLabel>
                    <TextField
                      name='jobInsurance.agentPhone'
                      placeholder={t('editJobDialog.jobInsurance.agentPhone')}
                      value={cast(form.values?.jobInsurance?.agentPhone)}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'jobInsurance.agentPhone'))}
                      helperText={getIn(form.errors, 'jobInsurance.agentPhone')}
                    />
                  </Box>
                  <Box className='w-full'></Box>
                </BaseRow>
              </>
            )}
            <Box className='text-[#000000] mb-[20px]'>{t('editJobDialog.assignedStaff')}</Box>
            <BaseRow className='gap-[20px] mb-[20px]'>
              {assignees && (
                <SelectAssignee
                  options={assignees}
                  name='jobAssigneeIds'
                  value={cast(form.values?.jobAssigneeIds)}
                  onChange={(value) => form.setFieldValue(`jobAssigneeIds`, value.target.value)}
                />
              )}
            </BaseRow>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()} disabled={form.isSubmitting}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditJobDialog);
